// src/services/audioStreamingService.ts
import SocketService from './socketService';
import { encodeWavInt16 } from '../utils/audio/wavEncoder';

// transcribe resamples everything to 16kHz mono, so capturing at the device rate (~48kHz) only
// buys bytes on the wire. Modern browsers honour this hint and resample natively.
const TARGET_SAMPLE_RATE = 16000;

// The AudioWorklet render quantum; the ScriptProcessor fallback is sliced to it so both run the VAD on the same window.
const RENDER_QUANTUM = 128;

// Mirrors AnalyserNode.getByteTimeDomainData, so voiceThreshold means what getInputLevel() reads.
export function frameLevel(frame: Float32Array): number {
	if (frame.length === 0) return 0;
	let sum = 0;
	for (let i = 0; i < frame.length; i++) {
		const byte = Math.min(255, Math.max(0, Math.floor(128 * (1 + frame[i]))));
		sum += Math.abs(byte - 128);
	}
	return sum / frame.length;
}

export interface CaptureStats {
	state: 'running' | 'suspended' | 'closed';
	frames: number;
	voiceMs: number;
	packets: number;
	queued: number;
	bytes: number;
}

export interface AudioStreamingServiceOptions {
	stream: MediaStream;
	roomId: string;
	participantId: string;
	roomType?: string; // NEW: Add roomType to skip speaker matching for online consults
	onVoiceDetected?: (active: boolean) => void;
	maxChunkSize?: number;
	voiceThreshold?: number;
	silenceTimeout?: number;
	preRollMs?: number;
}

export class AudioStreamingService {
	private stream: MediaStream;
	private roomId: string;
	private participantId: string;
	private roomType: string; // NEW
	private onVoiceDetected?: (active: boolean) => void;
	private maxChunkSize: number;
	private socketService: SocketService;

	private audioContext: AudioContext | null = null;
	public analyser: AnalyserNode | null = null;
	private sourceNode: MediaStreamAudioSourceNode | null = null;

	private audioWorkletNode: AudioWorkletNode | null = null;
	private scriptProcessor: ScriptProcessorNode | null = null;
	private recordedBuffers: Float32Array[] = [];
	private sampleRate: number = 0;
	private recordingStartTime: number = 0;

	// Frames captured while idle, kept so startRecording() can seed the chunk with the audio that
	// preceded the VAD trip — that is where the onset of the first word lives.
	private preRollBuffers: Float32Array[] = [];
	private preRollSamples = 0;
	private preRollMs: number;
	// Wall clock of the first sample currently held in recordedBuffers.
	private captureStartTimestamp = 0;

	private bufferLength: number = 0;
	private dataArray: Uint8Array | null = null;

	private voiceThreshold: number;
	private silenceTimeout: number;
	private samplesSinceVoice = 0;
	// Excludes the seeded pre-roll, so the first chunk is pre-roll plus maxChunkSize seconds.
	private samplesInChunk = 0;

	private isRecording = false;
	private isInitialized = false;
	private isVoiceActive = false;

	private isActive = false;
	private instanceId = Math.random().toString(36).substring(2, 9);
	private gestureResumeHandler: (() => void) | null = null;

	// Cumulative for the instance's whole life, never reset by stop(): useCaptureTelemetry reports per-instance deltas.
	private framesCaptured = 0;
	private voicedSamples = 0;
	private packetsHandedOff = 0;
	private packetsQueued = 0;
	private packetBytes = 0;

	constructor(options: AudioStreamingServiceOptions) {
		this.stream = options.stream;
		this.roomId = options.roomId;
		this.participantId = options.participantId;
		this.roomType = options.roomType || 'online'; // NEW: Default to 'online'
		this.onVoiceDetected = options.onVoiceDetected;
		this.maxChunkSize = options.maxChunkSize || 5;
		this.voiceThreshold = options.voiceThreshold ?? 2;
		this.silenceTimeout = options.silenceTimeout ?? 400;
		this.preRollMs = options.preRollMs ?? 400;
		this.socketService = SocketService.getInstance();
		console.log(`AudioStreamingService: New instance created [${this.instanceId}], type: ${this.roomType}`);
	}

	public async init(): Promise<boolean> {
		if (this.isInitialized) {
			console.warn(`AudioStreamingService [${this.instanceId}]: Already initialized.`);
			return true;
		}

		try {
			try {
				this.audioContext = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
			} catch (rateError) {
				// Some browsers reject an unsupported sampleRate hint outright. The device rate still
				// goes into the WAV header, so transcribe reads it off the header and resamples.
				console.warn(`AudioStreamingService [${this.instanceId}]: ${TARGET_SAMPLE_RATE}Hz AudioContext unsupported, falling back to the device rate.`);
				this.audioContext = new AudioContext();
			}
			// An AudioContext created outside a user gesture can begin 'suspended' (autoplay
			// policy), in which state the graph produces NO frames and the VAD never trips — a
			// silently dead transcription pipeline. Resume is best-effort; a context still
			// suspended afterwards is at least visible in the console instead of nowhere.
			if (this.audioContext.state === 'suspended') {
				try {
					await this.audioContext.resume();
				} catch {
					/* logged below either way */
				}
			}
			if (this.audioContext.state !== 'running') {
				console.warn(`AudioStreamingService [${this.instanceId}]: AudioContext is '${this.audioContext.state}' — no audio will be captured until it runs.`);
				this.armGestureResume();
			}
			this.sampleRate = this.audioContext.sampleRate;
			this.sourceNode = this.audioContext.createMediaStreamSource(this.stream);

			this.analyser = this.audioContext.createAnalyser();
			this.analyser.fftSize = 256;
			this.bufferLength = this.analyser.frequencyBinCount;
			this.dataArray = new Uint8Array(this.bufferLength);
			this.sourceNode.connect(this.analyser);

			if (this.audioContext.audioWorklet) {
				const recorderProcessorCode = `
					class RecorderProcessor extends AudioWorkletProcessor {
						constructor() {
							super();
						}
						process(inputs, outputs, parameters) {
							const input = inputs[0];
							if (input.length > 0) {
								let combined;
								if (input.length > 1) {
									const channelLength = input[0].length;
									combined = new Float32Array(channelLength);
									for (let i = 0; i < channelLength; i++) {
										let sum = 0;
										for (let ch = 0; ch < input.length; ch++) {
											sum += input[ch][i];
										}
										combined[i] = sum / input.length;
									}
								} else {
									combined = input[0];
								}
								this.port.postMessage(combined);
							}
							return true;
						}
					}
					registerProcessor("recorder-processor", RecorderProcessor);
				`;
				const blob = new Blob([recorderProcessorCode], { type: "application/javascript" });
				const url = URL.createObjectURL(blob);
				await this.audioContext.audioWorklet.addModule(url);
				this.audioWorkletNode = new AudioWorkletNode(this.audioContext, "recorder-processor");
				this.audioWorkletNode.port.onmessage = (event) => {
					this.acceptFrame(new Float32Array(event.data));
				};
				this.sourceNode.connect(this.audioWorkletNode);
				this.audioWorkletNode.connect(this.audioContext.destination);
				console.log(`AudioStreamingService [${this.instanceId}]: AudioWorklet initialized.`);
			} else {
				console.warn(`AudioStreamingService [${this.instanceId}]: AudioWorklet not supported, falling back to ScriptProcessorNode.`);
				const channels = this.sourceNode.channelCount || 1;
				this.scriptProcessor = this.audioContext.createScriptProcessor(4096, channels, 1);
				this.scriptProcessor.onaudioprocess = (event: AudioProcessingEvent) => {
					const inputBuffer = event.inputBuffer;
					const channelCount = inputBuffer.numberOfChannels;
					const bufferLength = inputBuffer.getChannelData(0).length;
					const combined = new Float32Array(bufferLength);
					for (let i = 0; i < bufferLength; i++) {
						let sum = 0;
						for (let ch = 0; ch < channelCount; ch++) {
							sum += inputBuffer.getChannelData(ch)[i];
						}
						combined[i] = sum / channelCount;
					}
					for (let i = 0; i < combined.length; i += RENDER_QUANTUM) {
						this.acceptFrame(combined.subarray(i, Math.min(i + RENDER_QUANTUM, combined.length)));
					}
				};
				this.sourceNode.connect(this.scriptProcessor);
				this.scriptProcessor.connect(this.audioContext.destination);
			}

			this.isInitialized = true;
			console.log(`AudioStreamingService [${this.instanceId}]: Initialized.`);
			return this.isInitialized;
		} catch (error) {
			console.error(`AudioStreamingService [${this.instanceId}]: Error during initialization:`, error);
			return false;
		}
	}

	// init() succeeding is not the same as audio flowing — a suspended context reads false here.
	public get isCapturing(): boolean {
		return this.isInitialized && this.audioContext?.state === 'running';
	}

	// Read from the analyser on demand, on the same scale as frameLevel().
	public getInputLevel(): number | null {
		if (!this.isCapturing || !this.analyser || !this.dataArray) return null;
		this.analyser.getByteTimeDomainData(this.dataArray);
		return this.dataArray.reduce((sum, val) => sum + Math.abs(val - 128), 0) / this.bufferLength;
	}

	public getCaptureStats(): CaptureStats {
		const contextState = this.audioContext?.state;
		return {
			// Safari's 'interrupted' is a live context that is not running, so it reads as suspended; stop() drops the context.
			state: !contextState || contextState === 'closed' ? 'closed' : contextState === 'running' ? 'running' : 'suspended',
			frames: this.framesCaptured,
			voiceMs: this.samplesToMs(this.voicedSamples),
			packets: this.packetsHandedOff,
			queued: this.packetsQueued,
			bytes: this.packetBytes,
		};
	}

	// A context suspended by autoplay policy resumes on the next user gesture, so borrow one.
	private armGestureResume() {
		if (this.gestureResumeHandler) {
			return;
		}

		const handler = async () => {
			try {
				await this.audioContext?.resume();
			} catch {
				/* the next gesture gets another go */
			}
			if (this.audioContext?.state !== 'suspended') {
				this.disarmGestureResume();
			}
		};

		this.gestureResumeHandler = handler;
		document.addEventListener('pointerdown', handler, { once: false });
		document.addEventListener('keydown', handler, { once: false });
	}

	private disarmGestureResume() {
		if (!this.gestureResumeHandler) {
			return;
		}

		document.removeEventListener('pointerdown', this.gestureResumeHandler);
		document.removeEventListener('keydown', this.gestureResumeHandler);
		this.gestureResumeHandler = null;
	}

	public start() {
		if (!this.audioContext || !this.analyser) {
			console.warn(`AudioStreamingService [${this.instanceId}]: Must init() before start().`);
			return;
		}

		if (this.isActive) {
			console.warn(`AudioStreamingService [${this.instanceId}]: Already active, ignoring duplicate start() call.`);
			return;
		}

		this.isActive = true;
		console.log(`AudioStreamingService [${this.instanceId}]: Starting voice activity monitoring...`);
	}

	/** Live VAD tuning: takes effect on the next audio frame, with no restart. */
	public setVadOptions(options: { voiceThreshold?: number; silenceTimeout?: number }) {
		if (typeof options.voiceThreshold === 'number') {
			this.voiceThreshold = options.voiceThreshold;
		}
		if (typeof options.silenceTimeout === 'number') {
			this.silenceTimeout = options.silenceTimeout;
		}
	}

	public stop() {
		console.log(`AudioStreamingService [${this.instanceId}]: Stopping...`);

		this.disarmGestureResume();

		if (!this.isActive && !this.isInitialized) {
			console.warn(`AudioStreamingService [${this.instanceId}]: Already stopped, ignoring duplicate stop() call.`);
			return;
		}

		this.isActive = false;

		this.stopRecording();

		if (this.audioContext) {
			this.audioContext.close().catch(e => console.warn(`AudioStreamingService [${this.instanceId}]: Error closing audio context:`, e));
			this.audioContext = null;
		}

		if (this.audioWorkletNode && this.sourceNode) {
			try {
				this.sourceNode.disconnect(this.audioWorkletNode);
				this.audioWorkletNode.disconnect();
			} catch (e) {
				console.warn(`AudioStreamingService [${this.instanceId}]: Error disconnecting AudioWorkletNode.`, e);
			}
			this.audioWorkletNode = null;
		}

		if (this.scriptProcessor && this.sourceNode) {
			try {
				this.sourceNode.disconnect(this.scriptProcessor);
				this.scriptProcessor.disconnect();
			} catch (e) {
				console.warn(`AudioStreamingService [${this.instanceId}]: Error disconnecting ScriptProcessorNode.`, e);
			}
			this.scriptProcessor.onaudioprocess = null;
			this.scriptProcessor = null;
		}

		this.isRecording = false;
		this.isInitialized = false;
		this.isVoiceActive = false;
		this.preRollBuffers = [];
		this.preRollSamples = 0;
		this.samplesSinceVoice = 0;
		this.samplesInChunk = 0;
		console.log(`AudioStreamingService [${this.instanceId}]: Stopped completely.`);
	}

	// The captured audio is the VAD's clock: requestAnimationFrame stops and timers are throttled in a hidden tab.
	private acceptFrame(frame: Float32Array) {
		this.framesCaptured += 1;
		if (this.isRecording) {
			this.recordedBuffers.push(frame);
			this.samplesInChunk += frame.length;
		} else {
			// Idle frames roll through a ring trimmed to preRollMs, so the word that trips the VAD keeps its onset.
			this.preRollBuffers.push(frame);
			this.preRollSamples += frame.length;

			const maxPreRollSamples = Math.round((this.preRollMs / 1000) * this.sampleRate);
			while (this.preRollSamples > maxPreRollSamples && this.preRollBuffers.length > 0) {
				const dropped = this.preRollBuffers.shift() as Float32Array;
				this.preRollSamples -= dropped.length;
			}
		}

		if (this.isActive && this.stepVoiceActivity(frameLevel(frame), frame.length)) {
			this.voicedSamples += frame.length;
		}
	}

	private samplesToMs(samples: number): number {
		if (!this.sampleRate) return 0;
		return Math.round((samples / this.sampleRate) * 1000);
	}

	// Returns whether this frame was voiced, so the capture stats count exactly what the VAD decided.
	private stepVoiceActivity(level: number, samples: number): boolean {
		const voiceDetected = level > this.voiceThreshold;

		if (voiceDetected) {
			this.samplesSinceVoice = 0;
			if (!this.isRecording) {
				this.startRecording();
			}
		} else {
			this.samplesSinceVoice += samples;
			if (this.isRecording && this.samplesToMs(this.samplesSinceVoice) > this.silenceTimeout) {
				this.stopRecording();
			}
		}

		if (this.isRecording && this.samplesInChunk >= this.maxChunkSize * this.sampleRate) {
			console.log(`AudioStreamingService [${this.instanceId}]: Max chunk size reached, chunking audio`);
			this.processChunkAndContinue();
		}

		if (voiceDetected && !this.isVoiceActive) {
			this.isVoiceActive = true;
			if (this.onVoiceDetected) {
				this.onVoiceDetected(true);
			}
		} else if (!voiceDetected && this.isVoiceActive && this.samplesToMs(this.samplesSinceVoice) > this.silenceTimeout) {
			this.isVoiceActive = false;
			if (this.onVoiceDetected) {
				this.onVoiceDetected(false);
			}
		}

		return voiceDetected;
	}

	private startRecording() {
		if (!this.audioContext || !this.sourceNode) {
			console.error(`AudioStreamingService [${this.instanceId}]: AudioContext not available.`);
			return;
		}

		if (this.isRecording) {
			console.warn(`AudioStreamingService [${this.instanceId}]: Already recording.`);
			return;
		}

		// Seed the chunk with the pre-roll. ONLY here — processChunkAndContinue() must never
		// re-seed, or every forced flush would replay the pre-roll window into the transcript.
		this.recordedBuffers = this.preRollBuffers;
		const seededSamples = this.preRollSamples;
		this.preRollBuffers = [];
		this.preRollSamples = 0;
		this.samplesInChunk = 0;

		this.isRecording = true;
		this.recordingStartTime = Date.now();
		// The first sample in the buffer is older than "now" by the pre-roll it was seeded with.
		this.captureStartTimestamp = this.recordingStartTime - this.samplesToMs(seededSamples);

		console.log(`AudioStreamingService [${this.instanceId}]: Started recording.`);
	}

	private processChunkAndContinue() {
		if (this.recordedBuffers.length === 0) return;

		const buffersCopy = [...this.recordedBuffers];
		this.processFinalRecording(buffersCopy, this.captureStartTimestamp);

		// Capture is continuous across the cut, so the next chunk starts exactly where this one
		// ended. NOTE: the flush still cuts mid-word — deliberately deferred, because overlapping
		// the chunks would duplicate words in the live transcript view. The final full-recording
		// pass is the designed fix for the boundary.
		this.captureStartTimestamp += this.samplesToMs(buffersCopy.reduce((sum, buf) => sum + buf.length, 0));

		this.recordedBuffers = [];
		this.samplesInChunk = 0;
		this.recordingStartTime = Date.now();
	}

	private stopRecording() {
		if (!this.isRecording) {
			return;
		}

		this.isRecording = false;

		if (this.recordedBuffers.length > 0) {
			this.processFinalRecording(this.recordedBuffers, this.captureStartTimestamp);
			this.recordedBuffers = [];
		}
	}

	private processFinalRecording(buffers: Float32Array[], audioStartTimestamp: number) {
		if (!buffers || buffers.length === 0) {
			console.log(`AudioStreamingService [${this.instanceId}]: No audio recorded.`);
			return;
		}

		try {
			const totalLength = buffers.reduce((sum, buf) => sum + buf.length, 0);
			if (totalLength === 0) {
				console.log(`AudioStreamingService [${this.instanceId}]: Empty audio buffers.`);
				return;
			}

			const combinedBuffer = new Float32Array(totalLength);
			let offset = 0;
			for (const buf of buffers) {
				if (buf && buf.length > 0) {
					combinedBuffer.set(buf, offset);
					offset += buf.length;
				}
			}

			// No normalisation pass: getUserMedia already runs autoGainControl at every capture
			// site, and a second gain stage only lifts the noise floor into Whisper's input.
			const wavBlob = encodeWavInt16(combinedBuffer, this.sampleRate);

			const audioId = `audio-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

			const reader = new FileReader();
			reader.onload = async () => {
				const arrayBuffer = reader.result as ArrayBuffer;
				const success = await this.socketService.sendAudio({
					roomId: this.roomId,
					participantId: this.participantId,
					roomType: this.roomType, // NEW: Include roomType
					timestamp: Date.now(),
					// Wall clock of the FIRST sample in this packet, pre-roll included. transcribe
					// anchors absoluteStart/absoluteEnd on it whenever it arrives and then reports
					// `timestampSource: "client_audio_start"`; drop it and it falls back to
					// `timestamp` above — stamped when the packet FINISHED, so up to maxChunkSize
					// seconds late — and reports `"client"`. `timestamp` is also what talk's
					// stale-chunk sweep reads, so the two are not interchangeable.
					audioStartTimestamp,
					// The rate actually captured at — the WAV header carries it too, but talk's
					// passthrough forwards this field rather than parsing the audio.
					sampleRate: this.sampleRate,
					audioId: audioId,
					data: arrayBuffer,
				});
				this.packetsHandedOff += 1;
				this.packetBytes += arrayBuffer.byteLength;
				if (!success) this.packetsQueued += 1;

				if (success) {
					console.log(`AudioStreamingService [${this.instanceId}]: Emitted audio ${audioId} (${arrayBuffer.byteLength} bytes, type: ${this.roomType})`);
				} else {
					console.warn(`AudioStreamingService [${this.instanceId}]: Audio ${audioId} queued for later`);
				}
			};

			reader.onerror = (error) => {
				console.error(`AudioStreamingService [${this.instanceId}]: Error reading WAV blob:`, error);
			};

			reader.readAsArrayBuffer(wavBlob);
		} catch (error) {
			console.error(`AudioStreamingService [${this.instanceId}]: Error processing audio:`, error);
		}
	}

}